import { Link } from "react-router-dom";
import { InstagramLogo, TiktokLogo, YoutubeLogo, PinterestLogo, FacebookLogo, EnvelopeSimple, Heart } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import AdBanner from "./AdBanner";

export default function Footer() {
  const { flags } = useSiteFlags();
  const [settings, setSettings] = useState({
    contact_email: "contactus@recipefiler.com",
    instagram_url: "https://instagram.com/recipefiler",
    tiktok_url: "https://tiktok.com/@recipefiler",
    youtube_url: "https://www.youtube.com/@recipefiler",
    pinterest_url: "https://uk.pinterest.com/recipefiler",
    facebook_url: "https://www.facebook.com/people/RecipeFiler/61591833807686/",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <AdBanner />
      <footer className="bg-rf-ink text-rf-cream relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h3 className="font-display text-3xl md:text-4xl font-black mb-4">
                Follow the story on social.
              </h3>
              <p className="text-rf-cream/70 mb-8 text-lg leading-relaxed max-w-md">
                We're <span className="text-rf-amber font-bold">@recipefiler</span> on Instagram, TikTok, YouTube, Pinterest and Facebook —
                fresh recipes, behind-the-scenes from real family kitchens, and every-week scanning tips.
              </p>
              <p className="overline text-rf-amber mb-4">Check them out and follow</p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram-link"
                  className="w-16 h-16 rounded-full bg-rf-amber/10 hover:bg-rf-amber text-rf-amber hover:text-rf-ink flex items-center justify-center transition-all hover:-translate-y-1"
                  aria-label="Instagram"
                >
                  <InstagramLogo size={32} weight="duotone" />
                </a>
                <a
                  href={settings.tiktok_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok-link"
                  className="w-16 h-16 rounded-full bg-rf-amber/10 hover:bg-rf-amber text-rf-amber hover:text-rf-ink flex items-center justify-center transition-all hover:-translate-y-1"
                  aria-label="TikTok"
                >
                  <TiktokLogo size={32} weight="duotone" />
                </a>
                <a
                  href={settings.youtube_url || "https://www.youtube.com/@recipefiler"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-youtube-link"
                  className="w-16 h-16 rounded-full bg-rf-amber/10 hover:bg-rf-amber text-rf-amber hover:text-rf-ink flex items-center justify-center transition-all hover:-translate-y-1"
                  aria-label="YouTube"
                >
                  <YoutubeLogo size={32} weight="duotone" />
                </a>
                <a
                  href={settings.pinterest_url || "https://uk.pinterest.com/recipefiler"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-pinterest-link"
                  className="w-16 h-16 rounded-full bg-rf-amber/10 hover:bg-rf-amber text-rf-amber hover:text-rf-ink flex items-center justify-center transition-all hover:-translate-y-1"
                  aria-label="Pinterest"
                >
                  <PinterestLogo size={32} weight="duotone" />
                </a>
                <a
                  href={settings.facebook_url || "https://www.facebook.com/people/RecipeFiler/61591833807686/"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-facebook-link"
                  className="w-16 h-16 rounded-full bg-rf-amber/10 hover:bg-rf-amber text-rf-amber hover:text-rf-ink flex items-center justify-center transition-all hover:-translate-y-1"
                  aria-label="Facebook"
                >
                  <FacebookLogo size={32} weight="duotone" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <p className="overline text-rf-amber mb-4">Explore</p>
              <ul className="space-y-3 text-rf-cream/80">
                <li><Link to="/directory" className="hover:text-rf-amber">Public Directory</Link></li>
                <li><Link to="/news" className="hover:text-rf-amber">Recipe Filer News</Link></li>
                <li><Link to="/shop" className="hover:text-rf-amber">Shop</Link></li>
                {flags.gamification_enabled && (
                  <li><Link to="/leaderboard" data-testid="footer-leaderboard-link" className="hover:text-rf-amber">Leaderboard</Link></li>
                )}
                {flags.subscriptions_enabled && <li><Link to="/pricing" className="hover:text-rf-amber">Pricing</Link></li>}
                <li><Link to="/import" data-testid="footer-import-link" className="hover:text-rf-amber">Import Recipes</Link></li>
                <li><Link to="/conversions" className="hover:text-rf-amber">Conversions</Link></li>
                <li><Link to="/diet" data-testid="footer-diet-link" className="hover:text-rf-amber">Special Diets</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-2">
              <p className="overline text-rf-amber mb-4">Help</p>
              <ul className="space-y-3 text-rf-cream/80">
                <li><Link to="/how-to-use" className="hover:text-rf-amber">How To Use</Link></li>
                <li><Link to="/faqs" className="hover:text-rf-amber">FAQs</Link></li>
                <li><Link to="/kitchen-help" data-testid="footer-kitchen-help-link" className="hover:text-rf-amber">Kitchen Help</Link></li>
                <li><Link to="/voice" data-testid="footer-voice-link" className="hover:text-rf-amber">Voice</Link></li>
                <li><Link to="/tags" data-testid="footer-tags-link" className="hover:text-rf-amber">All Tags</Link></li>
                <li><Link to="/ios-share-setup" data-testid="footer-ios-link" className="hover:text-rf-amber">iPhone Share Setup</Link></li>
                <li><Link to="/terms" data-testid="footer-terms-link" className="hover:text-rf-amber">Terms &amp; Conditions</Link></li>
                <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-rf-amber">Privacy Policy</Link></li>
                <li><Link to="/dmca" className="hover:text-rf-amber">DMCA / Takedown</Link></li>
                <li><Link to="/admin" data-testid="footer-admin-link" className="text-rf-cream/60 hover:text-rf-amber text-xs italic">Admin</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-2">
              <p className="overline text-rf-amber mb-4">Contact</p>
              <a
                href={`mailto:${settings.contact_email}`}
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 text-rf-cream hover:text-rf-amber"
              >
                <EnvelopeSimple size={20} weight="duotone" />
                <span className="text-sm">{settings.contact_email}</span>
              </a>
            </div>
          </div>

          <p className="mt-10 text-xs text-rf-cream/50 text-center sm:text-left" data-testid="footer-implicit-tc">
            By using this site you agree to our{" "}
            <Link to="/terms" className="underline hover:text-rf-amber">Terms &amp; Conditions</Link>
            {" "}and the placement of strictly-necessary cookies for sign-in. See our{" "}
            <Link to="/privacy" className="underline hover:text-rf-amber">Privacy Policy</Link>.
          </p>

          <div className="mt-6 pt-8 border-t border-rf-cream/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-rf-cream/50">
            <p>© {new Date().getFullYear()} <Link to="/terms" className="hover:text-rf-amber">RecipeFiler.com</Link>. All rights reserved.</p>
            <p className="inline-flex items-center gap-2">Made with <Heart size={16} weight="fill" className="text-rf-amber" /> in family kitchens</p>
          </div>
        </div>
      </footer>
    </>
  );
}
